import { FC, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { WarningCircleIcon, CheckCircleIcon, PulseIcon } from '@phosphor-icons/react';
import { useEndpoints } from '../../../hooks/useEndpoints';
import LeopartLoader from '../../../shared/LeopartLoader/LeopartLoader';
import CustomAvatar from '../../../shared/CustomAvatar/CustomAvatar';
import { CustomBadge } from '../../../shared/CustomBadge/CustomBadge';
import { ComplaintBadge } from '../../../shared/ComplaintBadge/ComplaintBadge';
import KpiCard from '../../../shared/KpiCard/KpiCard';
import Typhography from '../../../shared/Typhography/Typhography';
import { addBaseUrl } from '../../../lib/DurationPicker/utils';
import { ChartErrorState } from '../AthleteInjuryCharts/charts/_shared/ChartErrorState';
import DateNavigator from '../DateNavigator';
import styles from './TeamBodyMapDay.module.scss';
import { ZONE_LABELS, TYPE_LABELS, getIntensityLabel } from '../../../shared/bodyMap/bodyMapScale';

interface TeamBodyMapDayProps { teamId: string; }

interface ComplaintItem {
  zone: string;
  type: string;
  intensity: number;
  impact: string;
  note?: string;
}

interface BodyMapEntry {
  athleteId: string;
  firstName: string;
  lastName: string;
  image?: string;
  bodyMap: {
    _id: string;
    date: string;
    overallNote?: string;
    complaints: ComplaintItem[];
  } | null;
}

type IntensityBand = 'mild' | 'moderate' | 'intense';

/** Fascia di intensità (per scegliere la classe CSS), coerente con `getIntensityLabel`. */
function intensityBand(intensity?: number): IntensityBand {
  const label = getIntensityLabel(intensity ?? 1);
  if (label === 'Intensa') return 'intense';
  if (label === 'Moderata') return 'moderate';
  return 'mild';
}

const INT_DOT_CLASS: Record<IntensityBand, string> = {
  mild: styles.intMild,
  moderate: styles.intModerate,
  intense: styles.intIntense,
};

const TeamBodyMapDay: FC<TeamBodyMapDayProps> = ({ teamId }) => {
  const endpoints = useEndpoints();
  const [date, setDate] = useState<string>(() => dayjs().format('YYYY-MM-DD'));

  const dailyQ = useQuery<BodyMapEntry[]>({
    queryKey: ['body-map-team-daily', teamId, date],
    queryFn: async () => {
      const res = await endpoints.getBodyMapTeamDaily(teamId, { date });
      const data = (res?.data as any) ?? [];
      return Array.isArray(data) ? data : [];
    },
    enabled: !!teamId && !!date,
  });

  const entries = dailyQ.data ?? [];
  const stats = useMemo(() => {
    const compiled = entries.filter((e) => e.bodyMap !== null);
    const totalComplaints = compiled.reduce(
      (s, e) => s + (e.bodyMap?.complaints?.length ?? 0), 0,
    );
    const zonesSet = new Set<string>();
    compiled.forEach((e) => e.bodyMap?.complaints?.forEach((c) => zonesSet.add(c.zone)));
    return {
      total: entries.length,
      compiled: compiled.length,
      totalComplaints,
      zonesCount: zonesSet.size,
    };
  }, [entries]);

  return (
    <div className={styles.root}>
      {/* Header */}
      <div className={styles.headerRow}>
        <div className={styles.titleBlock}>
          <Typhography size="bold-20">Fastidi del giorno</Typhography>
          <Typhography size="regular-12" className={styles.subtitle}>
            Vista per data — {dayjs(date).format('dddd D MMMM YYYY')}
          </Typhography>
        </div>

        <DateNavigator selectedDate={date} onDateChange={setDate} />
      </div>

      {/* Aggregati */}
      <div className={styles.statsGrid}>
        <KpiCard
          label="Compilazione"
          value={`${stats.compiled}/${stats.total}`}
          sub="atleti con body map"
        />
        <KpiCard
          label="Zone segnalate"
          value={String(stats.zonesCount)}
          sub={`uniche su ${Object.keys(ZONE_LABELS).length}`}
        />
        <KpiCard
          label="Segnalazioni totali"
          value={String(stats.totalComplaints)}
          sub="complaints registrati"
        />
      </div>

      {/* Lista atleti */}
      {dailyQ.isLoading ? (
        <div className={styles.loading}><LeopartLoader /></div>
      ) : dailyQ.isError ? (
        <div className={styles.errorState}>
          <ChartErrorState onRetry={() => dailyQ.refetch()} />
        </div>
      ) : entries.length === 0 ? (
        <div className={styles.empty}>
          <PulseIcon size={36} weight="light" color="var(--c-text-3)" />
          <Typhography size="regular-14">Nessun atleta nel team.</Typhography>
        </div>
      ) : (
        <div className={styles.athleteGrid}>
          {entries.map((entry) => {
            const compiled = entry.bodyMap !== null;
            const complaints = entry.bodyMap?.complaints ?? [];
            const noComplaints = compiled && complaints.length === 0;
            const fullName = `${entry.firstName ?? ''} ${entry.lastName ?? ''}`.trim();
            const initials = `${(entry.firstName?.[0] ?? '').toUpperCase()}${(entry.lastName?.[0] ?? '').toUpperCase()}`;

            return (
              <div
                key={entry.athleteId}
                className={`${styles.athleteCard} ${!compiled ? styles.athleteCardEmpty : ''}`}
              >
                <div className={styles.athleteHeader}>
                  <CustomAvatar
                    name={initials || '·'}
                    src={entry.image ? addBaseUrl(1, entry.image) : undefined}
                    className={styles.avatar}
                  />
                  <div className={styles.athleteHeaderText}>
                    <Typhography size="bold-14" className={styles.athleteName}>
                      {fullName || `Atleta #${entry.athleteId.slice(-6)}`}
                    </Typhography>
                    <div className={styles.athleteStatus}>
                      {!compiled && (
                        <span className={styles.statusMissing}>
                          <WarningCircleIcon size={11} /> Non compilato
                        </span>
                      )}
                      {noComplaints && (
                        <CustomBadge label="Nessun fastidio" color="lightgreen" />
                      )}
                      {compiled && complaints.length > 0 && (
                        <ComplaintBadge complaints={complaints} size="sm" />
                      )}
                    </div>
                  </div>
                </div>

                {complaints.length > 0 && (
                  <ul className={styles.complaintList}>
                    {complaints.map((c, idx) => (
                      <li key={idx} className={styles.complaintRow}>
                        <span className={`${styles.intDot} ${INT_DOT_CLASS[intensityBand(c.intensity)]}`} />
                        <div className={styles.complaintMain}>
                          <Typhography size="bold-12" className={styles.zoneLabel}>
                            {ZONE_LABELS[c.zone] ?? c.zone}
                          </Typhography>
                          <Typhography size="regular-12" className={styles.complaintMeta}>
                            {c.type && <span>{TYPE_LABELS[c.type] ?? c.type}</span>}
                            <span> · {getIntensityLabel(c.intensity)}</span>
                            {c.note && <span> · {c.note}</span>}
                          </Typhography>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}

                {entry.bodyMap?.overallNote && (
                  <div className={styles.note}>
                    <Typhography size="bold-10" className={styles.noteLabel}>
                      <CheckCircleIcon size={9} /> Nota
                    </Typhography>
                    <Typhography size="regular-12" className={styles.noteText}>
                      {entry.bodyMap.overallNote}
                    </Typhography>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TeamBodyMapDay;
